<script setup>
/**
 * The Community's Lessons tab (community-simplify DESIGN.md §3; redone
 * 2026-10-28, lesson-redo DESIGN.md A, C, D5; the feed's card and bottom
 * 2026-10-30, community-feed-invite DESIGN.md). One job: find a lesson, or a
 * Mystic, about what's on your mind.
 *
 * Top to bottom, centred: the head and the one search field (LessonSearch).
 * While the field is empty: Around the fire (the trending lesson and one
 * stat) and the stream of LessonCards, newest first, with More lessons; at a
 * page seam, now and then, the invitation card (LessonInvite) drawn from what
 * the seeker has just read (utils/lesson-invite.js); then the feed's bottom
 * (LessonsFoot): "Looking for something?" and the same search again.
 *
 * Typing (2 or more characters, 200ms) folds the head and turns the page
 * below into results: "17 lessons about love · 3 Mystics" with Clear, up to
 * three Mystics (squares), then the lessons, 9 at a time, More lessons. No
 * lesson: any Mystics that matched, "No lesson about “…” yet.", and
 * "✦ Ask {Mystic} about it" seeding a reading with the typed words.
 *
 * The search log (API.md §2) hears Enter, a lesson tap and a Mystic tap, never
 * a keystroke. Recent searches stay on the device; so does the card's signal.
 *
 * Emits `open-lesson` (short_uuid) and `ask` (words, reader, via).
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import LessonCard from '@/components/ui/share/LessonCard.vue'
import LoadState from '@/components/ui/LoadState.vue'
import LessonSearch from './LessonSearch.vue'
import MysticResult from './MysticResult.vue'
import LessonInvite from './LessonInvite.vue'
import LessonsFoot from './LessonsFoot.vue'
import { getSeekerFeed, getCovenantOverview, searchAll, logSearch, getSearchSuggest } from '@/api-service'
import { track } from '@/utils/analytics'
import { leadIn, lessonCards, openMystic } from '@/utils/lesson'
import {
  HEAD, NEWEST, ASK, NOTICE_EVENT, noMatchTitle, moreLabel, fireStat, takeLessonsNotice, topicLabel,
} from '@/utils/community-lessons'
import { createInvite, HOUSE } from '@/utils/lesson-invite'
import { useLessonSearch } from '@/composables/useLessonSearch'
import { useLessonLinger } from '@/composables/useLessonLinger'

const emit = defineEmits(['open-lesson', 'ask'])

// ── The search (the head's field; the feed's bottom has its own copy) ──
const { query, q, searching, results, runSearch, noMatch, line, asker, onSubmit, onPick, clear: clearAll, setVia } = useLessonSearch()

// ── The invitation card's signal (community-feed-invite §4) ──
const invite = createInvite()
const rootEl = ref(null)
const pane = () => (rootEl.value && rootEl.value.closest ? rootEl.value.closest('.lessons-pane') : null)
const cardsShown = ref({})      // seam → the card drawn this mount
const revealed = new Set()      // seams whose card has drawn in (once per card)
const seen = new Set()          // seams whose card counted as shown
const past = new Set()          // seams scrolled past, waiting for the scroll to rest
let alive = true

// ── The stream (the field empty) ──
// `starts[n]` is where page n+1 begins: the seam after page n.
const feed = ref({ items: [], page: 1, hasMore: false, loading: false, failed: false, starts: {} })
async function loadFeed(reset = true) {
  const f = feed.value
  if (f.loading) return
  const page = reset ? 1 : f.page + 1
  const seam = reset ? 0 : f.page
  f.loading = true
  f.failed = false
  // Mystic Aida's lessons name the search's pick (Chris 2026-10-30): asked
  // while the page loads, so the card costs no extra wait.
  const early = seam && !invite.settled(seam) ? invite.decide(seam).card : null
  const pickEarly = early && early.house ? pickFor(early.topic) : null
  const data = await getSeekerFeed(page)
  if (data) {
    const items = data.items || []
    if (seam && items.length) await settleSeam(seam, early, pickEarly)
    if (reset) f.starts = {}
    if (seam && items.length) f.starts = { ...f.starts, [seam]: f.items.length }
    f.items = reset ? items : f.items.concat(items)
    f.hasMore = !!data.has_more
    f.page = page
  } else {
    f.failed = true
  }
  f.loading = false
}
function pickFor(topic) {
  return searchAll(topicLabel(topic) || topic, { page: 1, pageSize: 1 }).then((d) => {
    if (!d) return null
    if (d.ask && d.ask.sub_domain) return d.ask
    return (Array.isArray(d.mystics) ? d.mystics : []).find((m) => m && m.sub_domain && m.sub_domain !== HOUSE) || null
  }).catch(() => null)
}
// Decided once, when page seam+1 arrives; a plain seam stays plain for the visit.
async function settleSeam(seam, early, pickEarly) {
  const kept = invite.seamCard(seam)
  if (kept) { if (!kept.spent) cardsShown.value = { ...cardsShown.value, [seam]: kept }; return }
  if (invite.settled(seam)) return
  let card = invite.decide(seam).card
  if (card && card.house) {
    const ask = await (pickEarly && early && early.topic === card.topic ? pickEarly : pickFor(card.topic))
    if (!alive) return
    card = invite.resolve(card, ask)
  }
  if (!card) { invite.pass(seam); return }
  invite.place(card)
  cardsShown.value = { ...cardsShown.value, [seam]: invite.seamCard(seam) }
}
const cardAt = computed(() => {
  const out = {}
  const starts = feed.value.starts || {}
  Object.keys(cardsShown.value).forEach((seam) => {
    const at = starts[seam]
    if (at !== undefined && cardsShown.value[seam]) out[at] = cardsShown.value[seam]
  })
  return out
})
function onInviteShown(card) {
  if (seen.has(card.seam)) return
  seen.add(card.seam)
  track('lesson_invite', { act: 'shown', topic: card.topic, reader: card.sub })
}
function onInvitePast(card) {
  if (!seen.has(card.seam)) return
  invite.spend(card.seam)
  past.add(card.seam)
}
function dropCard(seam) {
  const next = { ...cardsShown.value }
  delete next[seam]
  cardsShown.value = next
  past.delete(seam)
}
function inviteMeet(card) {
  invite.spend(card.seam)
  track('lesson_invite', { act: 'meet', topic: card.topic, reader: card.sub })
  openMystic(card.sub, 'invite')
  dropCard(card.seam)
}
function inviteAsk(card) {
  invite.spend(card.seam)
  track('lesson_invite', { act: 'ask', topic: card.topic, reader: card.sub })
  emit('ask', '', card.sub, 'invite')
  dropCard(card.seam)
}
// Scrolled past: once the scroll has rested 300ms and the card is still out of
// sight above, it goes, and the scroll is lowered by its height in the same
// frame, so nothing moves on screen.
let rest = null
function onPaneScroll() {
  if (!past.size) return
  if (rest) clearTimeout(rest)
  rest = setTimeout(removePast, 300)
}
async function removePast() {
  rest = null
  const p = pane()
  const root = rootEl.value
  if (!root) return
  const top = p ? p.getBoundingClientRect().top : 0
  let lift = 0
  for (const seam of [...past]) {
    const el = root.querySelector(`[data-testid="lessons-invite-card"][data-seam="${seam}"]`)
    if (!el) { past.delete(seam); continue }
    const r = el.getBoundingClientRect()
    if (r.bottom > top) continue   // back in view: it stays until it is out again
    const grid = el.parentElement
    const gap = grid ? parseFloat(getComputedStyle(grid).rowGap) || 0 : 0
    lift += r.height + gap
    dropCard(seam)
  }
  if (!lift) return
  await nextTick()
  if (p) p.scrollTop -= lift
  else if (typeof window !== 'undefined') window.scrollBy(0, -lift)
}

// Lingering on a stream lesson (§4.3); activity keeps the visit.
const byId = (id) => feed.value.items.find((x) => x && x.short_uuid === id)
let touched = 0
const linger = useLessonLinger({
  root: pane,
  onRead: (id) => { const item = byId(id); if (item) invite.read(item, 'linger') },
  onActive: () => { const t = Date.now(); if (t - touched > 60000) { touched = t; invite.touch() } },
})
function observeStream() {
  nextTick(() => {
    const root = rootEl.value
    if (!root) return
    root.querySelectorAll('[data-testid="lessons-stream"] [data-lesson-id]').forEach((el) => linger.observe(el))
  })
}
watch(() => feed.value.items.length, observeStream)
watch(searching, (v) => { if (!v) observeStream() })

function openLesson(item, fromSearch = false) {
  if (!item || !item.short_uuid) return
  if (fromSearch && q.value) logSearch({ q: q.value, kind: 'tap' })
  // A stream lesson opened counts double toward the card (§4.3).
  if (!fromSearch && feed.value.items.includes(item)) invite.read(item, 'open')
  emit('open-lesson', item.short_uuid)
}
function openResultMystic(m) {
  if (!m || !m.sub_domain) return
  logSearch({ q: q.value, kind: 'tap', sub_domain: m.sub_domain })
  openMystic(m.sub_domain, 'search')
}
function ask() {
  track('lesson_ask', { via: 'search' })
  emit('ask', q.value, asker.value.sub)
}

// ── Suggestions: the placeholder's questions and People are asking about ──
const placeholders = ref([])
const asking = ref([])
async function loadSuggest() {
  const d = await getSearchSuggest()
  if (!d) return
  placeholders.value = Array.isArray(d.placeholders) ? d.placeholders : []
  asking.value = Array.isArray(d.asking) ? d.asking : []
}

// ── Around the fire (D5): the trending lesson and one stat ──
const trending = ref(null)
const stat = ref(null)
async function loadFire() {
  const data = await getCovenantOverview()
  const fire = data && data.fire
  trending.value = (fire && fire.trending && fire.trending.short_uuid) ? fire.trending : null
  stat.value = fireStat(fire && fire.stat)
}
const showFire = computed(() => !searching.value && (trending.value || stat.value))
const trendPic = computed(() => (trending.value && trending.value.image_url && !trending.value.cards_lead ? trending.value.image_url : ''))
const trendFan = computed(() => lessonCards(trending.value).filter((c) => c.image_url).slice(0, 4))
const trendSpoke = computed(() => Number(trending.value && (trending.value.spoke_count ?? trending.value.support_count)) || 0)
function searchStat() {
  if (!stat.value) return
  setVia('topic')
  query.value = stat.value.word
}

// ── A quiet line: a deep link to a lesson that isn't available (K2) ──
const notice = ref(takeLessonsNotice())
function onNotice(e) { notice.value = (e && e.detail && e.detail.text) || ''; takeLessonsNotice() }

let scroller = null
onMounted(() => {
  loadFeed(true)
  loadFire()
  loadSuggest()
  window.addEventListener(NOTICE_EVENT, onNotice)
  scroller = pane() || window
  scroller.addEventListener('scroll', onPaneScroll, { passive: true })
})
onUnmounted(() => {
  alive = false
  if (rest) clearTimeout(rest)
  window.removeEventListener(NOTICE_EVENT, onNotice)
  if (scroller) scroller.removeEventListener('scroll', onPaneScroll)
})

defineExpose({ query, loadFeed, runSearch })
</script>

<template>
  <div ref="rootEl" class="lx" data-testid="lessons-tab">
    <header class="hd" :class="{ compact: searching }">
      <div class="hd-words" :aria-hidden="searching ? 'true' : null">
        <h1 class="hd-h">{{ HEAD.title }}</h1>
        <p class="hd-line">{{ HEAD.line }}</p>
      </div>
      <LessonSearch v-model="query" :placeholders="placeholders" :asking="asking" @submit="onSubmit" @pick="onPick" />
      <p v-if="notice" class="lx-notice" role="status" data-testid="lessons-notice">{{ notice }}</p>
    </header>

    <!-- Results, in place under the field -->
    <section v-if="searching" class="rs" data-testid="lessons-results">
      <template v-if="!noMatch">
        <div v-if="results.done && !results.failed" class="rs-line" data-testid="lessons-result-line" aria-live="polite">
          <span><b>{{ line.count }} {{ line.noun }}</b> {{ line.about }}{{ line.mystics }}</span>
          <button type="button" class="rs-clear" data-testid="lessons-clear" @click="clearAll">Clear</button>
        </div>
        <div v-if="results.mystics.length" class="mys" aria-label="Mystics" data-testid="lessons-mystics">
          <MysticResult v-for="m in results.mystics" :key="m.sub_domain" :mystic="m" @open="openResultMystic" />
        </div>
        <p v-if="results.lessons.length" class="sec">Lessons</p>
        <div class="grid">
          <LessonCard v-for="(item, i) in results.lessons" :key="item.short_uuid || i" class="lx-card" :lesson="item" interactive @open="openLesson(item, true)" />
        </div>
        <LoadState v-if="!results.lessons.length" :loading="results.loading || !results.done" :failed="results.failed"
                   message="Couldn't search just now." loading-label="Searching" @retry="runSearch(true)" />
        <button v-if="results.hasMore && results.lessons.length" type="button" class="more-btn" :disabled="results.loading"
                data-testid="lessons-more" @click="runSearch(false)">{{ results.loading ? 'Drawing…' : moreLabel('') }}</button>
      </template>

      <!-- No lesson: never a dead end -->
      <template v-else>
        <template v-if="results.mystics.length">
          <div class="rs-line" data-testid="lessons-result-line">
            <span><b>{{ results.mystics.length }} Mystic{{ results.mystics.length === 1 ? '' : 's' }}</b> for “{{ results.q }}”</span>
            <button type="button" class="rs-clear" data-testid="lessons-clear" @click="clearAll">Clear</button>
          </div>
          <div class="mys" aria-label="Mystics" data-testid="lessons-mystics">
            <MysticResult v-for="m in results.mystics" :key="m.sub_domain" :mystic="m" @open="openResultMystic" />
          </div>
        </template>
        <div class="nomatch" data-testid="lessons-no-match" aria-live="polite">
          <p>{{ noMatchTitle({ query: results.q }) }}</p>
          <button type="button" data-testid="lessons-ask" @click="ask">{{ ASK.button(asker.name) }}</button>
        </div>
      </template>
    </section>

    <!-- The field empty: Around the fire, then the stream -->
    <div v-else class="feed-c">
      <section v-if="showFire" class="fire" aria-label="Around the fire" data-testid="lessons-fire">
        <p class="fire-h">✦ Around the fire</p>
        <div class="fire-row" :class="{ single: !(trending && stat) }">
          <button v-if="trending" type="button" class="tr" data-testid="fire-trending" @click="openLesson(trending)">
            <span class="tr-vis" aria-hidden="true">
              <img v-if="trendPic" :src="trendPic" alt="" />
              <span v-else class="tr-fan"><span v-for="(c, i) in trendFan" :key="i" class="c"><img :src="c.image_url" alt="" :class="{ rev: c.reversed }" /></span></span>
            </span>
            <span class="tr-txt">
              <span class="lab">Trending lesson</span>
              <span class="tr-q">{{ trending.lesson || leadIn(trending).text }}</span>
              <span v-if="trendSpoke >= 2" class="meta"><b>✦</b> Spoke to {{ trendSpoke }} people</span>
            </span>
          </button>
          <button v-if="stat" type="button" class="stt" data-testid="fire-stat" @click="searchStat">
            <span class="lab">{{ stat.label }}</span>
            <span class="stt-w">{{ stat.word }}</span>
            <span class="go" aria-hidden="true">›</span>
          </button>
        </div>
      </section>

      <section class="lx-stream" data-testid="lessons-stream">
        <p class="sec">{{ NEWEST }}</p>
        <div class="grid">
          <template v-for="(item, i) in feed.items" :key="item.short_uuid || i">
            <!-- A page seam: the invitation card, where More lessons was (community-feed-invite §4.2). -->
            <LessonInvite v-if="cardAt[i]" :key="`invite-${cardAt[i].seam}`" :card="cardAt[i]" :root="pane" :revealed="revealed.has(cardAt[i].seam)"
                          @reveal="revealed.add(cardAt[i].seam)" @shown="onInviteShown(cardAt[i])" @past="onInvitePast(cardAt[i])"
                          @meet="inviteMeet(cardAt[i])" @ask="inviteAsk(cardAt[i])" />
            <LessonCard class="lx-card" :lesson="item" :data-lesson-id="item.short_uuid" interactive @open="openLesson(item)" />
          </template>
        </div>
        <LoadState v-if="!feed.items.length" :loading="feed.loading" :failed="feed.failed"
                   message="Couldn't reach the lessons just now." loading-label="Gathering the lessons" @retry="loadFeed(true)" />
        <p v-if="!feed.items.length && !feed.loading && !feed.failed" class="lx-quiet">No lessons yet. The first one could be yours.</p>
        <button v-if="feed.hasMore && feed.items.length" type="button" class="more-btn" :disabled="feed.loading" data-testid="lessons-more"
                @click="loadFeed(false)">{{ feed.loading ? 'Drawing…' : moreLabel('') }}</button>
      </section>
    </div>

    <!-- The feed's bottom (community-feed-invite §3); hidden while the head's search answers. -->
    <LessonsFoot v-if="!searching" :placeholders="placeholders" :asking="asking"
                 @ask="(words, reader, via) => emit('ask', words, reader, via)" @open-lesson="emit('open-lesson', $event)" />
  </div>
</template>

<style scoped>
.lx {
  --gold: #c9a94e; --goldlt: #e7c983; --lilac: #c3a9ff; --mist: #b9a6d6; --dim: #8f7fb0; --plum: #2a1845;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  box-sizing: border-box; max-width: 1024px; margin: 0 auto; padding: 0 0 24px; color: #efe4ff; font-family: var(--ui);
}
.lx *, .lx *::before, .lx *::after { box-sizing: border-box; }
.lx img { display: block; }

/* The head: centred; it folds away while results show, so the field rises. */
.hd { max-width: 760px; margin: 0 auto; text-align: center; padding: 30px 20px 0; }
.hd-words { overflow: hidden; max-height: 200px; opacity: 1; transition: max-height .35s cubic-bezier(.2, .8, .2, 1), opacity .25s ease; }
.hd.compact .hd-words { max-height: 0; opacity: 0; }
.hd-h { margin: 0 0 8px; font: 600 40px/1.05 var(--serif); color: #fbf3ff; letter-spacing: -.005em; }
.hd-line { margin: 0; font: 500 15px/1.5 var(--ui); color: #c9b8e6; }
.hd.compact :deep(.sx) { margin-top: 6px; }
.lx-notice { margin: 14px auto 0; font: italic 500 16px var(--serif); color: var(--mist); }

.sec { margin: 0 0 14px; font: 600 10.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: #cdb98a; }
.grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
.grid > .lx-card { width: 100%; max-width: none; }
.lx-quiet { padding: 8px 2px; font: italic 500 16px var(--serif); color: var(--mist); }
.more-btn { display: block; margin: 26px auto 0; height: 46px; padding: 0 24px; border-radius: 23px; border: 1px solid rgba(231, 201, 131, .45); background: none;
  color: #ffe9b8; font: 600 14px var(--ui); cursor: pointer; }
.more-btn:hover:not(:disabled) { border-color: rgba(240, 217, 163, .8); }
.more-btn:focus-visible, .rs-clear:focus-visible { outline: 2px solid var(--goldlt); outline-offset: 2px; }

.feed-c { max-width: 944px; margin: 0 auto; padding: 26px 16px 30px; }
.rs { max-width: 944px; margin: 0 auto; padding: 0 16px; }
.rs-line { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin: 22px 0 14px; color: #c9b8e6; font-size: 15px; }
.rs-line b { font: 600 22px var(--serif); color: #fbf3ff; }
.rs-clear { flex: none; border: 0; background: none; color: #c9b8e6; font: 500 14px var(--ui); text-decoration: underline; text-underline-offset: 3px; cursor: pointer;
  min-height: 44px; min-width: 44px; padding: 0 0 0 8px; margin: -10px 0; }
.mys { display: grid; grid-template-columns: 1fr; gap: 10px; margin-bottom: 26px; }
.nomatch { margin: 30px 0 26px; text-align: center; }
.nomatch p { margin: 0; font: 600 23px/1.2 var(--serif); color: #fff6e4; overflow-wrap: anywhere; }
.nomatch button { margin-top: 10px; min-height: 44px; border: 0; background: none; cursor: pointer; font: 600 16px var(--ui); color: #f0d9a3;
  text-decoration: underline; text-decoration-color: rgba(240, 217, 163, .5); text-underline-offset: 4px; }
.nomatch button:focus-visible { outline: 2px solid var(--goldlt); outline-offset: 2px; border-radius: 4px; }

/* Around the fire: one trending lesson and one stat. */
.fire { margin: 0 0 30px; }
.fire-h { margin: 0 0 10px; font: 600 10.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: var(--goldlt); }
.fire-row { display: grid; grid-template-columns: 1fr; gap: 10px; }
.tr, .stt { position: relative; border: 1px solid rgba(231, 201, 131, .28); background: rgba(255, 255, 255, .04); border-radius: 16px; cursor: pointer; text-align: left;
  color: inherit; font: inherit; transition: border-color .2s, background .2s, transform .2s; }
.tr:hover, .stt:hover { border-color: rgba(240, 217, 163, .6); background: rgba(255, 255, 255, .07); transform: translateY(-1px); }
.tr:focus-visible, .stt:focus-visible { outline: 2px solid var(--goldlt); outline-offset: 2px; }
.tr { display: grid; grid-template-columns: 96px minmax(0, 1fr); padding: 0; overflow: hidden; min-height: 104px; }
.tr-vis { position: relative; overflow: hidden; background: var(--plum); }
.tr-vis > img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform .8s cubic-bezier(.2, .8, .2, 1); }
.tr:hover .tr-vis > img { transform: scale(1.06); }
.tr-fan { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: radial-gradient(70% 90% at 50% 55%, #4b2a7a, #160a2b); }
.tr-fan .c { width: 27px; aspect-ratio: 2 / 3; margin: 0 -3px; border-radius: 3px; overflow: hidden; border: 1px solid rgba(231, 201, 131, .85); }
.tr-fan .c img { width: 100%; height: 100%; object-fit: cover; }
.tr-fan .c img.rev { transform: rotate(180deg); }
.tr-txt { display: flex; flex-direction: column; justify-content: center; gap: 5px; padding: 12px 14px; min-width: 0; }
.lab { font: 600 9.5px/1.4 var(--display); letter-spacing: .18em; text-transform: uppercase; color: #d9bf7e; }
.tr-q { font: italic 600 19px/1.15 var(--serif); color: #fff6e4; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; overflow: hidden; text-wrap: balance; }
.meta { font: 500 12.5px var(--ui); color: var(--mist); }
.meta b { color: var(--goldlt); font-weight: 600; }
.stt { display: flex; flex-direction: row; align-items: baseline; flex-wrap: wrap; gap: 4px 10px; padding: 12px 40px 12px 16px; min-height: 52px; }
.stt-w { font: 600 22px/1.05 var(--serif); color: #ffe9b8; }
.stt .go { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); color: var(--gold); font-size: 20px; }

@media (max-width: 767px) {
  .hd { padding-top: 22px; }
  .hd-h { font-size: 32px; }
}
@media (min-width: 640px) {
  .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; align-items: stretch; }
}
@media (min-width: 768px) {
  .fire-row { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 14px; }
  .fire-row.single { grid-template-columns: 1fr; }
  .tr { grid-template-columns: 124px minmax(0, 1fr); min-height: 124px; }
  .tr-fan .c { width: 34px; }
  .tr-txt { padding: 14px 18px; }
  .tr-q { font-size: 21px; }
  .stt { flex-direction: column; align-items: flex-start; justify-content: center; gap: 4px; padding: 14px 20px; }
  .stt-w { font-size: 32px; }
  .mys { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
  .nomatch p { font-size: 26px; }
}
@media (min-width: 1024px) {
  .grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; }
  .feed-c, .rs { padding-left: 0; padding-right: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hd-words, .tr, .stt, .tr-vis > img { transition: none; }
  .tr:hover, .stt:hover { transform: none; }
}
</style>
